import {
  Benevole,
  BenevoleDetail,
  ElementKey,
  GraphqlKey,
  HabilitationCategory,
  PiwigoLevel,
} from "@/models";
import { Http } from "@/utils/http";

/** Backend calls for volunteer ("bénévole") management, including habilitations and Piwigo photo-library access. */
export default class BenevoleService {
  /** Lists all volunteers. */
  public static async benevoles(): Promise<Benevole[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoles`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /** Fetches full detail for a single volunteer. */
  public static async benevole(id: string): Promise<BenevoleDetail> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevole&id=${id}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /** Lists a volunteer's habilitations, grouped by category. */
  public static async habilitations(
    userId: string
  ): Promise<HabilitationCategory[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleHabilitations&id=${userId}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data ?? [];
  }

  /** Grants or revokes a single habilitation for a volunteer. */
  public static async toggleHabilitation(
    userId: string,
    habilitationId: string,
    active: boolean
  ): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleHabilitationUpdate`;
    await Http.addPostSettings();
    await Http.instance.post(
      targetUrl,
      Http.qsStringify({
        user_id: userId,
        habilitation_id: habilitationId,
        active: active ? 1 : 0,
      })
    );
  }

  /** Updates a volunteer's progress ("step") on a given competence. */
  public static async updateCompetence(
    userId: string,
    competenceId: string,
    stepId: string,
    commentaire: string | null
  ): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleUpdateCompetence`;
    await Http.instance.post(
      targetUrl,
      { user: userId, competence: competenceId, step: stepId, commentaire },
      { headers: { "Content-Type": "application/json" } }
    );
  }

  /** Lists Slack workspace members not yet registered as volunteers, so one can be onboarded. */
  public static async canAddFromSlack(): Promise<
    {
      id: string;
      username: string;
      nom_usage: string;
      picture: string;
      telephone: string | null;
    }[]
  > {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleCanAddFromSlack&q=`;
    const { data: response } = await Http.instance.get(targetUrl);
    if (!response.data) return [];
    return Object.entries(response.data)
      .filter(([key]) => !isNaN(Number(key)))
      .map(
        ([, value]) =>
          value as {
            id: string;
            username: string;
            nom_usage: string;
            picture: string;
            telephone: string | null;
          }
      );
  }

  /** Creates a new volunteer. @returns The new volunteer's id, or `null` on failure. */
  public static async create(benevole: {
    prenom: string;
    nom: string;
    nom_usage: string;
    username: string;
    slack_id: string;
    telephone: string;
  }): Promise<string | null> {
    const response = await Http.postTask(
      "benevoleCreate",
      { benevole },
      "Bénévole créé"
    );
    return response.data?.id ?? null;
  }

  /** Reads a volunteer's access level on the Piwigo photo library. */
  public static async piwigoGetAccess(userId: string): Promise<PiwigoLevel> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=piwigoGetAccess&id=${userId}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return (response.data?.level as PiwigoLevel) ?? "NONE";
  }

  /** Sets a volunteer's access level on the Piwigo photo library. */
  public static async piwigoSetAccess(
    userId: string,
    level: PiwigoLevel
  ): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=piwigoSetAccess`;
    await Http.addPostSettings();
    await Http.instance.post(
      targetUrl,
      Http.qsStringify({ user_id: userId, level })
    );
  }

  /** Lists a volunteer's GraphQL API keys. */
  public static async graphqlKeys(userId: string): Promise<GraphqlKey[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=graphqlKeys&id=${userId}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data ?? [];
  }

  /** Creates a new GraphQL API key for a volunteer. @returns The created key, or `null` on failure. */
  public static async graphqlKeyCreate(
    userId: string,
    libelle: string,
    tablesAllowed: string | null
  ): Promise<GraphqlKey | null> {
    const response = await Http.postTask<GraphqlKey>(
      "graphqlKeyCreate",
      { user_id: userId, libelle, tables_allowed: tablesAllowed ?? "" },
      "Clé GraphQL créée"
    );
    return response.data ?? null;
  }

  /** Updates a GraphQL API key's label and allowed tables. */
  public static async graphqlKeyUpdate(
    key: string,
    libelle: string,
    tablesAllowed: string | null
  ): Promise<void> {
    await Http.postTask(
      "graphqlKeyUpdate",
      { key, libelle, tables_allowed: tablesAllowed ?? "" },
      "Clé GraphQL mise à jour"
    );
  }

  /** Revokes (deletes) a GraphQL API key. */
  public static async graphqlKeyDelete(key: string): Promise<void> {
    await Http.postTask("graphqlKeyDelete", { key }, "Clé GraphQL supprimée");
  }

  /** Updates an existing volunteer's fields. */
  public static async update(benevole: Benevole): Promise<ElementKey> {
    const response = await Http.postTask("benevoleUpdate", { benevole });
    return response.data?.id;
  }
}
